import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Tooltip from '@material-ui/core/Tooltip';
import InfoOutlined from '@material-ui/icons/InfoOutlined';

import AccessLevelService, { gold } from 'services/AccessLevelService';
import { translate } from 'services/i18nService';
import UserStore from 'stores/UserStore';
import CustomCheckbox from 'components/CustomCheckbox/CustomCheckbox';
import ErrorAlert from 'components/ErrorAlert/ErrorAlert';
import { generateID } from 'utils/helpers';

import IconTokens from '../../../../../images/profilePage/tokens-grey.png';

import styles from './SendGiftForm.css';


const IDS = {
  prefix: '7Dhytyog',
  element: 'SendGiftForm',
  privateMessageArea: 'privateMessageArea',
};

const makeId = generateID(IDS.prefix, IDS.element);
const MESSAGE_START = 0;
const MAX_MESSAGE_LENGTH = 120;

const SendGiftForm = (props) => {
  const {
    anonymous,
    canSendCustomPrice,
    customPrice,
    gift,
    handlePriceChange,
    insufficientBalance,
    isDesktop,
    isGuestBilling,
    privateMessage,
    setPrivateMessage,
    toggleCheckbox,
  } = props;
  const { user: { accessLevel } } = UserStore;

  const isGoldUser = AccessLevelService.isMin(gold, accessLevel);
  const charLeft = MAX_MESSAGE_LENGTH - privateMessage.length;

  const handlePrivateMessage = useCallback((event) => {
    const formattedMessage = event.target.value.replace(/^\s+/g, '');
    const trimmedMessage = formattedMessage.slice(MESSAGE_START, MAX_MESSAGE_LENGTH);
    setPrivateMessage(trimmedMessage);
  }, [setPrivateMessage]);

  const disabledClass = classNames({ [styles.disabled]: insufficientBalance });

  const charsClass = classNames({
    [styles.charLeft]: true,
    [styles.disabled]: insufficientBalance,
  });

  return (
    <div className={styles.sendgiftFormContainer}>
      <div className={styles.modalGiftSummary}>
        <img
          alt={gift.name}
          className={styles.giftImage}
          src={gift?.details?.imageUrl}
        />
        <div className={styles.giftDetails}>
          <p className={styles.giftName}>
            {gift.name}
          </p>
          {canSendCustomPrice ? (
            <div className={styles.adminPrice}>
              <span>Value:</span>
              <input
                className={styles.adminPrice}
                min={0}
                type="number"
                value={customPrice}
                onChange={handlePriceChange}
              />
            </div>
          ) : (
            <p className={styles.price}>
              <img
                alt="tokens"
                className={styles.img}
                src={IconTokens}
              />
              {gift.price}
            </p>
          )}
        </div>
      </div>
      {isGoldUser && (
        <div className={styles.checkboxWrapper}>
          <CustomCheckbox
            checkboxClass={styles.check}
            checked={anonymous}
            className={styles.checkbox}
            disabled={insufficientBalance}
            id="send-anonymous-gift"
            label={translate('v2profile.send.as.anonymous')}
            labelClass={styles.label}
            value="checkbox"
            onChange={toggleCheckbox}
          />
          <Tooltip
            enterTouchDelay={0}
            title={translate('v2profile.anonymous.gift.tooltip')}
          >
            <InfoOutlined className={styles.infoIcon} />
          </Tooltip>
        </div>
      )}
      <div className={styles.messageArea}>
        <p className={disabledClass}>
          {translate('v2profile.gifts.private.message.label')}
        </p>
        <textarea
          className={styles.messageInput}
          disabled={insufficientBalance || isGuestBilling}
          id={makeId(IDS.privateMessageArea)}
          maxLength={MAX_MESSAGE_LENGTH}
          placeholder={translate('v2profile.gifts.type.message')}
          rows="3"
          value={privateMessage}
          onChange={handlePrivateMessage}
        />
        <span className={charsClass}>
          {translate(
            'v2profile.gifts.send.privateMessage',
            { 0: charLeft }
          )}
        </span>
      </div>
      {(insufficientBalance && isDesktop) && (
        <ErrorAlert message={translate('v2profile.insufficient.balance')} />
      )}
    </div>
  );
};

SendGiftForm.propTypes = {
  setPrivateMessage: PropTypes.func.isRequired,
  toggleCheckbox: PropTypes.func.isRequired,
  anonymous: PropTypes.bool,
  canSendCustomPrice: PropTypes.bool,
  customPrice: PropTypes.number,
  gift: PropTypes.object,
  handlePriceChange: PropTypes.func,
  insufficientBalance: PropTypes.bool,
  isDesktop: PropTypes.bool,
  isGuestBilling: PropTypes.bool,
  privateMessage: PropTypes.string,
};

SendGiftForm.defaultProps = {
  anonymous: false,
  canSendCustomPrice: false,
  customPrice: undefined,
  gift: undefined,
  handlePriceChange: () => undefined,
  insufficientBalance: false,
  isGuestBilling: false,
  isDesktop: false,
  privateMessage: undefined,
};

export default SendGiftForm;
